import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	PlusIcon,
	TrashIcon,
	ArrowTopRightOnSquareIcon,
	PhotoIcon,
} from '@heroicons/react/20/solid';
import client from '../../api/client';
import Spinner from '../Global/Spinner';
import { useAlert } from '../../context/AlertContext';

const money = (n, dp = 0) =>
	n === null || n === undefined
		? '—'
		: new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: dp,
		  }).format(Number(n));

const num = (n) => new Intl.NumberFormat('en-US').format(Math.round(Number(n) || 0));

// A fresh proposal skeleton (matches the server model defaults).
const blank = () => ({
	name: 'Solar Proposal',
	status: 'draft',
	state: 'MD',
	county: '',
	customer: '',
	design: {
		panelCount: 0,
		panelWattage: 440,
		systemSizeKw: 0,
		annualProductionKwh: 0,
		year1UsageKwh: '',
	},
	lineItems: [],
	financials: {
		utilityRatePerKwh: 0.24,
		utilityEscalatorPct: 4,
		panelDegradationPct: 0.4,
		srecPerMwh: 80,
		srecAnnualDeclinePct: 15,
		srecYears: 15,
		termYears: 30,
		priceAdjustmentPct: 0,
		rebate: 0,
		stateGrant: 0,
	},
	financing: {
		type: 'cash',
		aprPct: 6.99,
		termYears: 25,
		ratePerKwh: 0.13,
		escalatorPct: 0,
	},
});

const inputCls =
	'block w-full rounded-md border-0 bg-white/5 py-2 px-3 text-sm text-white ring-1 ring-inset ring-white/10 placeholder:text-gray-500 focus:ring-2 focus:ring-primary-500';

// Proposal images are served from the public API.
const IMG_BASE =
	typeof window !== 'undefined' && window.location.href.includes('localhost')
		? 'http://localhost:5053/api/public/proposal-image'
		: '/api/public/proposal-image';

export default function ProposalBuilder() {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { setAlert } = useAlert();

	const [proposal, setProposal] = useState(null);
	const [catalog, setCatalog] = useState(null);
	const [computed, setComputed] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Load catalog + (existing proposal or a fresh skeleton).
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const catRes = await client.get('/proposal/catalog');
				if (!cancelled) setCatalog(catRes.data.catalog);
				if (id) {
					const { data } = await client.get(`/proposal/${id}`);
					if (!cancelled) {
						setProposal(normalize(data.proposal));
						setComputed(data.computed);
					}
				} else {
					const p = blank();
					const presetCustomer = searchParams.get('customer');
					if (presetCustomer) p.customer = presetCustomer;
					if (!cancelled) setProposal(p);
				}
			} catch (err) {
				setAlert('error', err?.response?.data?.error || 'Failed to load');
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [id, searchParams, setAlert]);

	// Recompute (debounced) whenever the proposal changes.
	useEffect(() => {
		if (!proposal) return;
		const t = setTimeout(async () => {
			try {
				const { data } = await client.post('/proposal/preview', proposal);
				setComputed(data);
			} catch {
				/* keep prior computed on transient error */
			}
		}, 300);
		return () => clearTimeout(t);
	}, [proposal]);

	const update = useCallback((patch) => {
		setProposal((prev) => ({ ...prev, ...patch }));
	}, []);
	const updateDesign = (patch) =>
		setProposal((prev) => ({ ...prev, design: { ...prev.design, ...patch } }));
	const updateFin = (patch) =>
		setProposal((prev) => ({
			...prev,
			financials: { ...prev.financials, ...patch },
		}));
	const updateFinancing = (patch) =>
		setProposal((prev) => ({
			...prev,
			financing: { ...prev.financing, ...patch },
		}));

	// Auto-derive system size from panels × wattage when both present.
	useEffect(() => {
		if (!proposal) return;
		const { panelCount, panelWattage } = proposal.design;
		const kw = ((Number(panelCount) || 0) * (Number(panelWattage) || 0)) / 1000;
		if (kw && kw !== proposal.design.systemSizeKw) {
			updateDesign({ systemSizeKw: Math.round(kw * 100) / 100 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [proposal?.design.panelCount, proposal?.design.panelWattage]);

	const addLineItem = (preset) => {
		const item = preset
			? {
					category: preset.category,
					name: preset.name,
					unit: preset.unit,
					listPrice: preset.listPrice,
					quantity: preset.unit === 'each' ? proposal.design.panelCount || 1 : 1,
					discountPct: 0,
			  }
			: { category: 'other', name: '', unit: 'flat', listPrice: 0, quantity: 1, discountPct: 0 };
		update({ lineItems: [...proposal.lineItems, item] });
	};
	const updateLineItem = (i, patch) => {
		const items = proposal.lineItems.map((it, idx) =>
			idx === i ? { ...it, ...patch } : it
		);
		update({ lineItems: items });
	};
	const removeLineItem = (i) =>
		update({ lineItems: proposal.lineItems.filter((_, idx) => idx !== i) });

	const save = async () => {
		setSaving(true);
		try {
			if (id) {
				const { data } = await client.put(`/proposal/${id}`, proposal);
				setProposal(normalize(data.proposal));
				setComputed(data.computed);
				setAlert('success', 'Proposal saved');
			} else {
				const { data } = await client.post('/proposal', proposal);
				setAlert('success', 'Proposal created');
				navigate(`/admin/proposals/${data.proposal._id}`);
			}
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Save failed');
		} finally {
			setSaving(false);
		}
	};

	const uploadImages = async (files) => {
		if (!id || !files?.length) return;
		const fd = new FormData();
		Array.from(files).forEach((f) => fd.append('images', f));
		try {
			const { data } = await client.post(`/proposal/${id}/images`, fd, {
				headers: { 'Content-Type': 'multipart/form-data' },
			});
			setProposal((prev) => ({ ...prev, images: data.proposal.images }));
			setAlert('success', 'Images uploaded');
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Image upload failed');
		}
	};

	const removeImage = async (imageId) => {
		try {
			const { data } = await client.delete(`/proposal/${id}/images/${imageId}`);
			setProposal((prev) => ({ ...prev, images: data.proposal.images }));
		} catch (err) {
			setAlert('error', 'Could not remove image');
		}
	};

	const allProducts = useMemo(() => {
		if (!catalog) return [];
		return [
			...catalog.panels.map((p) => ({ ...p, category: 'panel' })),
			...catalog.inverters.map((p) => ({ ...p, category: 'inverter' })),
			...catalog.racking.map((p) => ({ ...p, category: 'racking' })),
			...catalog.warranties.map((p) => ({ ...p, category: 'warranty' })),
			...catalog.adders.map((p) => ({ ...p, category: 'adder' })),
		];
	}, [catalog]);

	if (loading || !proposal) {
		return (
			<div className='py-20'>
				<Spinner color='white' />
			</div>
		);
	}

	const fin = computed?.financials;
	const pricing = computed?.pricing;

	return (
		<div className='px-4 pb-24 sm:px-6 lg:px-8'>
			<div className='flex flex-wrap items-center justify-between gap-3'>
				<Link
					to='/admin/proposals'
					className='inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-white'
				>
					<ArrowLeftIcon className='h-4 w-4' /> Proposals
				</Link>
				<div className='flex items-center gap-3'>
					{id && (
						<a
							href={`/proposal/${id}`}
							target='_blank'
							rel='noreferrer'
							className='inline-flex items-center gap-2 rounded-md bg-white/5 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/10 hover:bg-white/10'
						>
							<ArrowTopRightOnSquareIcon className='h-4 w-4' /> Preview
						</a>
					)}
					<button
						onClick={save}
						disabled={saving}
						className='rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50'
					>
						{saving ? 'Saving…' : id ? 'Save' : 'Create'}
					</button>
				</div>
			</div>

			<input
				value={proposal.name}
				onChange={(e) => update({ name: e.target.value })}
				className='mt-4 w-full max-w-lg bg-transparent text-2xl font-semibold text-white focus:outline-none'
				placeholder='Proposal name'
			/>

			<div className='mt-6 grid grid-cols-1 gap-6 xl:grid-cols-5'>
				{/* LEFT: inputs */}
				<div className='space-y-6 xl:col-span-3'>
					{/* Customer + location */}
					<Card title='Customer & location'>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
							<Field label='State'>
								<input value={proposal.state} onChange={(e) => update({ state: e.target.value })} className={inputCls} />
							</Field>
							<Field label='County'>
								<input value={proposal.county} onChange={(e) => update({ county: e.target.value })} className={inputCls} />
							</Field>
							<Field label='Status'>
								<select value={proposal.status} onChange={(e) => update({ status: e.target.value })} className={inputCls}>
									{['draft', 'sent', 'accepted', 'declined'].map((s) => (
										<option key={s} value={s} className='bg-ink-900 capitalize'>{s}</option>
									))}
								</select>
							</Field>
						</div>
					</Card>

					{/* Design */}
					<Card title='Design (from Aurora)'>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
							<Field label='# of panels'>
								<input type='number' value={proposal.design.panelCount} onChange={(e) => updateDesign({ panelCount: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Panel wattage (W)'>
								<input type='number' value={proposal.design.panelWattage} onChange={(e) => updateDesign({ panelWattage: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='System size (kW)'>
								<input type='number' value={proposal.design.systemSizeKw} onChange={(e) => updateDesign({ systemSizeKw: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Annual production (kWh)'>
								<input type='number' value={proposal.design.annualProductionKwh} onChange={(e) => updateDesign({ annualProductionKwh: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Yr-1 usage (kWh)'>
								<input type='number' value={proposal.design.year1UsageKwh} onChange={(e) => updateDesign({ year1UsageKwh: Number(e.target.value) })} className={inputCls} />
							</Field>
						</div>
					</Card>

					{/* Products */}
					<Card title='Products'>
						<div className='mb-3 flex flex-wrap items-center gap-2'>
							<AddProductMenu products={allProducts} onPick={addLineItem} />
							<button onClick={() => addLineItem(null)} className='inline-flex items-center gap-1 rounded-md bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-200 ring-1 ring-white/10 hover:bg-white/10'>
								<PlusIcon className='h-3.5 w-3.5' /> Custom line
							</button>
						</div>
						{proposal.lineItems.length === 0 ? (
							<p className='text-sm text-gray-500'>No products yet. Add panels, inverter, racking, warranty, adders.</p>
						) : (
							<div className='space-y-2'>
								{proposal.lineItems.map((it, i) => {
									const priced = pricing?.pricedItems?.[i];
									return (
										<div key={i} className='grid grid-cols-12 items-center gap-2 rounded-md bg-white/5 px-3 py-2 ring-1 ring-white/10'>
											<input value={it.name} onChange={(e) => updateLineItem(i, { name: e.target.value })} placeholder='Item name' className='col-span-4 bg-transparent text-sm text-white focus:outline-none' />
											<select value={it.unit} onChange={(e) => updateLineItem(i, { unit: e.target.value })} className='col-span-2 rounded bg-white/5 px-1 py-1 text-xs text-gray-200 ring-1 ring-white/10'>
												<option value='each' className='bg-ink-900'>$/each</option>
												<option value='watt' className='bg-ink-900'>$/W</option>
												<option value='flat' className='bg-ink-900'>flat</option>
											</select>
											<input type='number' step='0.01' value={it.listPrice} onChange={(e) => updateLineItem(i, { listPrice: Number(e.target.value) })} className='col-span-2 rounded bg-white/5 px-2 py-1 text-right text-xs text-white ring-1 ring-white/10' title='List price' />
											<input type='number' value={it.quantity} onChange={(e) => updateLineItem(i, { quantity: Number(e.target.value) })} className='col-span-1 rounded bg-white/5 px-1 py-1 text-right text-xs text-white ring-1 ring-white/10' title='Qty' />
											<span className='col-span-2 text-right text-sm font-medium text-gray-200'>{money(priced?.netTotal, 0)}</span>
											<button onClick={() => removeLineItem(i)} className='col-span-1 justify-self-end rounded p-1 text-gray-500 hover:text-rose-400'>
												<TrashIcon className='h-4 w-4' />
											</button>
										</div>
									);
								})}
							</div>
						)}
						<div className='mt-3 flex justify-between border-t border-white/10 pt-3 text-sm'>
							<span className='text-gray-400'>Net subtotal</span>
							<span className='font-semibold text-white'>{money(pricing?.netSubtotal, 0)}</span>
						</div>
					</Card>

					{/* Images */}
					<Card title='Images'>
						{!id ? (
							<p className='text-sm text-gray-500'>
								Save the proposal first, then you can add design renders and
								photos here.
							</p>
						) : (
							<ImageUploader
								images={proposal.images || []}
								onUpload={uploadImages}
								onRemove={removeImage}
							/>
						)}
					</Card>

					{/* Pricing adjustments */}
					<Card title='Pricing adjustments'>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
							<Field label='Price adj (%)'>
								<input type='number' step='0.5' value={proposal.financials.priceAdjustmentPct} onChange={(e) => updateFin({ priceAdjustmentPct: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Rebate ($)'>
								<input type='number' value={proposal.financials.rebate} onChange={(e) => updateFin({ rebate: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='State grant ($)'>
								<input type='number' value={proposal.financials.stateGrant} onChange={(e) => updateFin({ stateGrant: Number(e.target.value) })} className={inputCls} />
							</Field>
						</div>
					</Card>

					{/* Financial assumptions */}
					<Card title='Financial assumptions'>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
							<Field label='Utility rate ($/kWh)'>
								<input type='number' step='0.001' value={proposal.financials.utilityRatePerKwh} onChange={(e) => updateFin({ utilityRatePerKwh: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Utility escalator (%)' hint='1–5'>
								<input type='number' min='1' max='5' step='0.5' value={proposal.financials.utilityEscalatorPct} onChange={(e) => updateFin({ utilityEscalatorPct: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Panel degradation (%)'>
								<input type='number' step='0.1' value={proposal.financials.panelDegradationPct} onChange={(e) => updateFin({ panelDegradationPct: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='SREC ($/MWh)'>
								<input type='number' value={proposal.financials.srecPerMwh} onChange={(e) => updateFin({ srecPerMwh: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='SREC decline (%/yr)'>
								<input type='number' value={proposal.financials.srecAnnualDeclinePct} onChange={(e) => updateFin({ srecAnnualDeclinePct: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='SREC years'>
								<input type='number' value={proposal.financials.srecYears} onChange={(e) => updateFin({ srecYears: Number(e.target.value) })} className={inputCls} />
							</Field>
							<Field label='Term (years)'>
								<input type='number' value={proposal.financials.termYears} onChange={(e) => updateFin({ termYears: Number(e.target.value) })} className={inputCls} />
							</Field>
						</div>
					</Card>

					{/* Financing */}
					<Card title='Financing'>
						<div className='mb-4 inline-flex rounded-md bg-white/5 p-1 ring-1 ring-white/10'>
							{['cash', 'loan', 'lease'].map((t) => (
								<button key={t} onClick={() => updateFinancing({ type: t })} className={`rounded px-4 py-1.5 text-sm font-medium capitalize transition-colors ${proposal.financing.type === t ? 'bg-primary-500 text-white' : 'text-gray-300 hover:text-white'}`}>
									{t}
								</button>
							))}
						</div>
						{proposal.financing.type === 'loan' && (
							<div className='grid grid-cols-2 gap-4'>
								<Field label='APR (%)'>
									<input type='number' step='0.01' value={proposal.financing.aprPct} onChange={(e) => updateFinancing({ aprPct: Number(e.target.value) })} className={inputCls} />
								</Field>
								<Field label='Loan term (years)'>
									<input type='number' value={proposal.financing.termYears} onChange={(e) => updateFinancing({ termYears: Number(e.target.value) })} className={inputCls} />
								</Field>
							</div>
						)}
						{proposal.financing.type === 'lease' && (
							<div className='grid grid-cols-2 gap-4'>
								<Field label='Lease rate ($/kWh)'>
									<input type='number' step='0.001' value={proposal.financing.ratePerKwh} onChange={(e) => updateFinancing({ ratePerKwh: Number(e.target.value) })} className={inputCls} />
								</Field>
								<Field label='Lease escalator (%)'>
									<input type='number' step='0.1' value={proposal.financing.escalatorPct} onChange={(e) => updateFinancing({ escalatorPct: Number(e.target.value) })} className={inputCls} />
								</Field>
							</div>
						)}
						{proposal.financing.type === 'cash' && (
							<p className='text-sm text-gray-500'>Paid up front. Net system cost is the year-1 outlay.</p>
						)}
					</Card>
				</div>

				{/* RIGHT: live output */}
				<div className='xl:col-span-2'>
					<div className='sticky top-20 space-y-4'>
						<div className='rounded-lg bg-white/5 p-5 ring-1 ring-white/10'>
							<h3 className='text-sm font-semibold uppercase tracking-wide text-gray-400'>Summary</h3>
							<div className='mt-4 grid grid-cols-2 gap-4'>
								<Metric label='Net system cost' value={money(pricing?.netSystemCost)} />
								<Metric
									label={proposal.financing.type === 'cash' ? 'Up-front' : 'Monthly payment'}
									value={proposal.financing.type === 'cash' ? money(pricing?.netSystemCost) : `${money(fin?.monthlyPayment)}/mo`}
									highlight
								/>
								<Metric label='Payback' value={fin?.summary?.paybackYear ? `Year ${fin.summary.paybackYear}` : '—'} />
								<Metric label={`${fin?.summary?.termYears || 30}-yr savings`} value={money(fin?.summary?.lifetimeSavings)} highlight />
								<Metric label='Monthly SREC (Yr 1)' value={money(fin?.monthlySrecYear1)} />
								<Metric label='Avg annual ROI' value={fin?.summary?.avgAnnualRoiPct != null ? `${fin.summary.avgAnnualRoiPct}%` : '—'} />
							</div>
						</div>

						{/* Cash-flow table */}
						<div className='max-h-[28rem] overflow-y-auto rounded-lg ring-1 ring-white/10'>
							<table className='min-w-full text-xs'>
								<thead className='sticky top-0 bg-ink-800 text-ink-400'>
									<tr>
										{['Yr', 'kWh', 'Rate', 'Savings', 'SREC', 'Cash flow', 'Cumulative'].map((h) => (
											<th key={h} className='px-2 py-2 text-right font-semibold first:text-left'>{h}</th>
										))}
									</tr>
								</thead>
								<tbody className='divide-y divide-white/5'>
									{(fin?.rows || []).map((r) => (
										<tr key={r.year} className={r.cumulativeCashFlow >= 0 ? 'bg-emerald-500/5' : ''}>
											<td className='px-2 py-1.5 text-left text-gray-300'>{r.year}</td>
											<td className='px-2 py-1.5 text-right text-gray-400'>{num(r.productionKwh)}</td>
											<td className='px-2 py-1.5 text-right text-gray-400'>${r.utilityRate.toFixed(3)}</td>
											<td className='px-2 py-1.5 text-right text-gray-300'>{money(r.utilitySavings)}</td>
											<td className='px-2 py-1.5 text-right text-gray-400'>{r.srecRevenue ? money(r.srecRevenue) : '—'}</td>
											<td className='px-2 py-1.5 text-right text-gray-300'>{money(r.annualCashFlow)}</td>
											<td className={`px-2 py-1.5 text-right font-medium ${r.cumulativeCashFlow >= 0 ? 'text-emerald-300' : 'text-gray-300'}`}>{money(r.cumulativeCashFlow)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function ImageUploader({ images, onUpload, onRemove }) {
	const inputRef = React.useRef(null);
	const [dragging, setDragging] = useState(false);
	return (
		<div>
			<div
				onClick={() => inputRef.current?.click()}
				onDragOver={(e) => {
					e.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragging(false);
					onUpload(e.dataTransfer.files);
				}}
				className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-center transition-colors ${
					dragging
						? 'border-primary-400 bg-primary-500/10'
						: 'border-white/20 hover:border-white/40'
				}`}
			>
				<PhotoIcon className='h-6 w-6 text-primary-400' />
				<span className='text-sm font-medium text-gray-200'>
					Drag & drop images, or click to browse
				</span>
				<span className='text-xs text-gray-500'>
					Design renders, roof photos, layout · up to 10 each
				</span>
			</div>
			<input
				ref={inputRef}
				type='file'
				accept='image/*'
				multiple
				className='hidden'
				onChange={(e) => onUpload(e.target.files)}
			/>
			{images.length > 0 && (
				<div className='mt-4 grid grid-cols-3 gap-3'>
					{images.map((img) => (
						<div key={img._id} className='group relative'>
							<img
								src={`${IMG_BASE}/${img.storedName}`}
								alt={img.originalName || 'proposal'}
								className='h-24 w-full rounded-md object-cover ring-1 ring-white/10'
							/>
							<button
								onClick={() => onRemove(img._id)}
								className='absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100'
								title='Remove'
							>
								<TrashIcon className='h-3.5 w-3.5' />
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function AddProductMenu({ products, onPick }) {
	const [open, setOpen] = useState(false);
	return (
		<div className='relative'>
			<button onClick={() => setOpen((o) => !o)} className='inline-flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600'>
				<PlusIcon className='h-3.5 w-3.5' /> Add product
			</button>
			{open && (
				<div className='absolute z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-md bg-ink-800 py-1 shadow-xl ring-1 ring-white/10'>
					{products.map((p, i) => (
						<button
							key={i}
							onClick={() => {
								onPick(p);
								setOpen(false);
							}}
							className='flex w-full items-center justify-between px-3 py-2 text-left text-xs text-gray-200 hover:bg-white/10'
						>
							<span>{p.name}</span>
							<span className='ml-2 text-gray-500'>
								{p.unit === 'watt' ? `$${p.listPrice}/W` : p.unit === 'flat' ? `$${p.listPrice}` : `$${p.listPrice}`}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

function Card({ title, children }) {
	return (
		<div className='rounded-lg bg-white/5 p-5 ring-1 ring-white/10'>
			<h3 className='mb-4 text-sm font-semibold uppercase tracking-wide text-gray-400'>{title}</h3>
			{children}
		</div>
	);
}

function Field({ label, hint, children }) {
	return (
		<div>
			<label className='mb-1 block text-xs font-medium text-gray-400'>
				{label}
				{hint && <span className='ml-1 text-gray-600'>({hint})</span>}
			</label>
			{children}
		</div>
	);
}

function Metric({ label, value, highlight }) {
	return (
		<div>
			<div className='text-xs text-gray-400'>{label}</div>
			<div className={`mt-0.5 font-semibold ${highlight ? 'text-lg text-primary-300' : 'text-base text-white'}`}>{value}</div>
		</div>
	);
}

// Strip populated/server-only bits so the object round-trips cleanly on save.
function normalize(p) {
	return {
		...p,
		customer: p.customer?._id || p.customer || '',
	};
}
